import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedComponent } from '../../shared/shared.component';
import { AlertService } from '../_services/alert.service';
import { AccountService } from '../_services/domainservice/account.service';
import { LoginService } from '../_services/domainservice/login.service';
import { ClientService } from '../_services/domainservice/Client.service';


@Component({
  selector: 'app-verifyotp',
  templateUrl: './verifyotp.component.html',
  styleUrls: ['./verifyotp.component.css']
})
export class VerifyotpComponent implements OnInit {
  form: FormGroup;
  target = '';
  challenge = '';
  loading = false;
  resending = false;
  errorMessage = '';
  successMessage = '';
  verified = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly loginService: LoginService,
    private readonly clientService: ClientService,
  ) {
    this.form = this.fb.group({
      code: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.target = this.route.snapshot.queryParamMap.get('target') || '';
    this.challenge = this.route.snapshot.queryParamMap.get('challenge') || '';
    if (!this.target) {
      // Nothing to verify against — this route is unreachable without arriving from Login.ts's
      // REQUIRES_2FA branch, which always supplies `target`.
      this.errorMessage = 'Missing account email. Please sign in again to request a new code.';
    }
  }

  get f() { return this.form.controls; }

  submit(): void {
    this.errorMessage = '';
    this.successMessage = '';
    if (this.form.invalid || !this.target) { return; }

    this.loading = true;
    const code = this.form.value.code;

    if (this.challenge) {
      // Real path: exchange the challenge token + code for an authenticated session.
      this.loginService.completeTwoFactor(this.challenge, code).subscribe({
        next: (res: any) => this.onTwoFactorComplete(res),
        error: (err: any) => this.onVerifyError(err),
      });
      return;
    }

    // Fallback path (no challenge token available): generic contact-code confirmation only, no
    // session is issued — see this component's header comment.
    this.loginService.verifyOtp(this.target, code).subscribe({
      next: () => {
        this.loading = false;
        this.verified = true;
        this.successMessage = 'Verified. Please sign in again to continue.';
        setTimeout(() => this.router.navigate(['/login']), 1500);
      },
      error: (err: any) => this.onVerifyError(err),
    });
  }

  private onTwoFactorComplete(res: any): void {
    if (res?.accessToken) {
      localStorage.setItem('accessToken', res.accessToken);
      localStorage.setItem('token', res.accessToken);
      localStorage.setItem('refreshToken', res.refreshToken || '');
    }

    const user = res?.user || {};
    this.loading = false;
    this.verified = true;

    const clientId = user.clientRegistrationUId || null;

    // Mirrors Login.ts's tryLogin() next handler exactly (same two branches: no tenant yet ->
    // onboarding, otherwise resolve the tenant's ClientRegistration row then route).
    if (!clientId) {
      this.setUserInStorage(user);
      this.successMessage = 'Verified. Redirecting...';
      this.router.navigate(['/clientinfo']);
      return;
    }

    this.clientService.getClient(clientId.toString()).subscribe({
      next: (data: any) => {
        let client: any = null;
        if (Array.isArray(data)) {
          const idKey = clientId.toString().toLowerCase();
          client = data.find((c: any) => (c?.clientRegistrationUId || '').toString().toLowerCase() === idKey)
            || data[0] || null;
        } else {
          client = data || null;
        }
        this.storeSessionAndRoute(user, clientId, client);
      },
      error: () => this.storeSessionAndRoute(user, clientId, null),
    });
  }

  private storeSessionAndRoute(user: any, clientId: any, client: any | null): void {
    const companyName = client?.companyName ?? null;
    const logo = client?.logo ?? null;
    const productId = user.productUId ?? client?.productUId ?? null;

    const sessionUser = {
      ...user,
      clientRegistrationUId: clientId,
      productUId: productId,
      companyName,
      logo
    };

    this.setUserInStorage(sessionUser);
    if (client) { localStorage.setItem('currentClient', JSON.stringify(client)); }
    localStorage.setItem('CurrentClientUId', clientId);
    if (productId) { localStorage.setItem('CurrentProductUId', productId); }
    if (companyName) { localStorage.setItem('CompanyName', companyName); }
    if (logo) { localStorage.setItem('CompanyLogo', logo); }
    if (user.email) { localStorage.setItem('EmailId', user.email); }

    this.successMessage = 'Verified. Redirecting...';
    this.router.navigateByUrl('/dashboard');
  }

  private setUserInStorage(user: any) {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  private onVerifyError(err: any): void {
    this.loading = false;
    const code2 = err?.error?.code;
    this.errorMessage =
      code2 === 'EXPIRED' ? 'That code has expired. Request a new one below.' :
        code2 === 'TOO_MANY' ? 'Too many attempts. Request a new code below.' :
          code2 === 'NOT_FOUND' ? 'No active code found. Request a new one below.' :
            code2 === 'INVALID' ? 'Incorrect code, or this challenge has expired. Request a new code below.' :
              'Incorrect code. Please try again.';
  }

  resend(): void {
    if (!this.target) { return; }
    this.errorMessage = '';
    this.successMessage = '';
    this.resending = true;
    this.loginService.sendOtp('email', this.target).subscribe({
      next: () => {
        this.resending = false;
        this.successMessage = 'A new code has been sent.';
      },
      error: () => {
        this.resending = false;
        this.errorMessage = 'Could not resend the code. Please try again.';
      },
    });
  }

  backToLogin(): void {
    this.router.navigate(['/login']);
  }
}
